import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, LinearProgress, Tabs, Tab, Paper,
  ImageList, ImageListItem, ImageListItemBar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';
import PhotoAddIcon from '@mui/icons-material/AddAPhoto';
import {
  MY_PHOTO_ALBUMS_QUERY, MY_CEREMONIES_QUERY,
  CREATE_PHOTO_ALBUM_MUTATION, UPDATE_PHOTO_ALBUM_MUTATION, DELETE_PHOTO_ALBUM_MUTATION,
  ADD_ALBUM_PHOTO_MUTATION, UPDATE_ALBUM_PHOTO_MUTATION, DELETE_ALBUM_PHOTO_MUTATION,
} from '../graphql/queries';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const ALBUM_TYPE_COLOR = { ceremony:'primary', pre_wedding:'secondary', reception:'warning', candid:'info', family:'success', other:'default' };
const STATUS_COLOR = { draft:'default', processing:'warning', ready:'success', shared:'info' };
const ALBUM_TYPES = ['ceremony','pre_wedding','reception','candid','family','other'];

const EMPTY_ALBUM = { name:'', albumType:'ceremony', vendorName:'', vendorContact:'', deliveryDate:'', shareUrl:'', description:'', notes:'', ceremonyId:'' };
const EMPTY_PHOTO = { url:'', thumbnail:'', caption:'', tags:'' };

export default function PhotoAlbumPage() {
  const [tab, setTab]           = useState(0);
  const [error, setError]       = useState('');
  const [albumDialog, setAlbumDialog] = useState(false);
  const [editAlbum, setEditAlbum]     = useState(null);
  const [albumForm, setAlbumForm]     = useState(EMPTY_ALBUM);
  const [activeAlbumId, setActiveAlbumId] = useState('');
  const [photoDialog, setPhotoDialog] = useState(false);
  const [editPhoto, setEditPhoto]     = useState(null);
  const [photoForm, setPhotoForm]     = useState(EMPTY_PHOTO);
  const [confirmDel, setConfirmDel]   = useState(null);
  const [confirmDelPhoto, setConfirmDelPhoto] = useState(null);

  const { data, loading, refetch } = useQuery(MY_PHOTO_ALBUMS_QUERY, { fetchPolicy:'cache-and-network' });
  const { data: cerData }          = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy:'cache-and-network' });

  const albums    = data?.myPhotoAlbums || [];
  const ceremonies = cerData?.myCeremonies || [];
  const activeAlbum = albums.find(a=>a.id===activeAlbumId);

  const [createAlbum] = useMutation(CREATE_PHOTO_ALBUM_MUTATION, { onCompleted:()=>{ setAlbumDialog(false); setAlbumForm(EMPTY_ALBUM); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [updateAlbum] = useMutation(UPDATE_PHOTO_ALBUM_MUTATION, { onCompleted:()=>{ setAlbumDialog(false); setEditAlbum(null); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [deleteAlbum] = useMutation(DELETE_PHOTO_ALBUM_MUTATION, { onCompleted:()=>{ setConfirmDel(null); if(activeAlbumId===confirmDel) setActiveAlbumId(''); refetch(); } });
  const [addPhoto]    = useMutation(ADD_ALBUM_PHOTO_MUTATION, { onCompleted:()=>{ setPhotoDialog(false); setPhotoForm(EMPTY_PHOTO); refetch(); }, onError:e=>setError(e.message) });
  const [updatePhoto] = useMutation(UPDATE_ALBUM_PHOTO_MUTATION, { onCompleted:()=>{ setPhotoDialog(false); setEditPhoto(null); refetch(); }, onError:e=>setError(e.message) });
  const [deletePhoto] = useMutation(DELETE_ALBUM_PHOTO_MUTATION, { onCompleted:()=>{ setConfirmDelPhoto(null); refetch(); } });

  const openEditAlbum = (a) => { setEditAlbum(a); setAlbumForm({ name:a.name||'', albumType:a.albumType||'ceremony', vendorName:a.vendorName||'', vendorContact:a.vendorContact||'', deliveryDate:a.deliveryDate||'', shareUrl:a.shareUrl||'', description:a.description||'', notes:a.notes||'', ceremonyId:a.ceremonyId||'' }); setAlbumDialog(true); };
  const openEditPhoto = (p) => { setEditPhoto(p); setPhotoForm({ url:p.url||'', thumbnail:p.thumbnail||'', caption:p.caption||'', tags:(p.tags||[]).join(',') }); setPhotoDialog(true); };
  const handleSaveAlbum = () => {
    const vars = { ...albumForm, ceremonyId: albumForm.ceremonyId || null };
    editAlbum ? updateAlbum({ variables:{ id:editAlbum.id, ...vars } }) : createAlbum({ variables: vars });
  };
  const handleSavePhoto = () => {
    const tagsArr = photoForm.tags ? photoForm.tags.split(',').map(t=>t.trim()).filter(Boolean) : [];
    if (editPhoto) updatePhoto({ variables:{ id:editPhoto.id, caption:photoForm.caption, tags:tagsArr } });
    else addPhoto({ variables:{ albumId:activeAlbumId, url:photoForm.url, thumbnail:photoForm.thumbnail||photoForm.url, caption:photoForm.caption, tags:tagsArr } });
  };

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Photo Albums</Typography>
          <Typography variant="body2" color="text.secondary">Organise your wedding photos by ceremony — share albums with family</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon/>} onClick={()=>{ setEditAlbum(null); setAlbumForm(EMPTY_ALBUM); setAlbumDialog(true); }}>New Album</Button>
      </Stack>
      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{mb:2}}/>}

      <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{mb:3}}>
        <Tab label={`My Albums (${albums.length})`}/>
        <Tab label="Photo Gallery" disabled={!activeAlbumId}/>
      </Tabs>

      {tab===0 && (
        <Grid container spacing={2}>
          {albums.length===0 && !loading && (
            <Grid item xs={12}><Typography color="text.secondary" textAlign="center" mt={4}>No albums yet. Create your first wedding photo album.</Typography></Grid>
          )}
          <Paged items={albums}>{rows => rows.map(a=>(
            <Grid item xs={12} sm={6} md={4} key={a.id}>
              <Card variant="outlined" onClick={()=>{ setActiveAlbumId(a.id); setTab(1); }} sx={{cursor:'pointer',height:'100%'}}>
                {a.coverPhotoUrl && (
                  <Box sx={{height:160,overflow:'hidden',borderBottom:'1px solid',borderColor:'divider'}}>
                    <img src={a.coverPhotoUrl} alt={a.name} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'}/>
                  </Box>
                )}
                {!a.coverPhotoUrl && (
                  <Box sx={{height:100,display:'flex',alignItems:'center',justifyContent:'center',bgcolor:'grey.100'}}>
                    <PhotoLibraryIcon sx={{fontSize:40,color:'grey.400'}}/>
                  </Box>
                )}
                <CardContent>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontWeight={700}>{a.name}</Typography>
                    <Chip size="small" label={a.status} color={STATUS_COLOR[a.status]||'default'}/>
                  </Stack>
                  <Stack direction="row" spacing={0.5} mt={0.5}>
                    <Chip size="small" label={a.albumType} color={ALBUM_TYPE_COLOR[a.albumType]||'default'} variant="outlined"/>
                    <Typography variant="caption" sx={{alignSelf:'center',ml:'auto'}}>{a.totalPhotos||0} photos</Typography>
                  </Stack>
                  {a.deliveryDate && <Typography variant="caption" color="text.secondary">Delivered: {a.deliveryDate}</Typography>}
                  <Stack direction="row" spacing={0.5} mt={1}>
                    <Tooltip title="Edit"><IconButton size="small" onClick={e=>{e.stopPropagation();openEditAlbum(a);}}><EditIcon fontSize="small"/></IconButton></Tooltip>
                    {a.shareUrl && <Tooltip title="Share Link"><IconButton size="small" color="primary" onClick={e=>{e.stopPropagation();window.open(a.shareUrl,'_blank');}}><ShareIcon fontSize="small"/></IconButton></Tooltip>}
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={e=>{e.stopPropagation();setConfirmDel(a.id);}}><DeleteIcon fontSize="small"/></IconButton></Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}</Paged>
        </Grid>
      )}

      {tab===1 && activeAlbum && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{activeAlbum.name}</Typography>
              <Typography variant="body2" color="text.secondary">{activeAlbum.totalPhotos||0} photos · {activeAlbum.albumType}</Typography>
            </Box>
            <Button variant="outlined" startIcon={<PhotoAddIcon/>} onClick={()=>{ setEditPhoto(null); setPhotoForm(EMPTY_PHOTO); setPhotoDialog(true); }}>Add Photo</Button>
          </Stack>
          {(activeAlbum.photos||[]).length===0 && (
            <Box sx={{textAlign:'center',py:6,color:'text.secondary'}}>
              <PhotoLibraryIcon sx={{fontSize:64,opacity:.2}}/>
              <Typography mt={1}>No photos in this album yet. Add photo URLs to populate the gallery.</Typography>
            </Box>
          )}
          <ImageList variant="quilted" cols={3} rowHeight={200} gap={8}>
            {(activeAlbum.photos||[]).map(p=>(
              <ImageListItem key={p.id}>
                <img src={p.thumbnail||p.url} alt={p.caption||'photo'} loading="lazy" style={{objectFit:'cover',width:'100%',height:'100%'}} onError={e=>{ e.target.src='data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect fill="%23eee" width="200" height="200"/><text fill="%23aaa" font-family="sans-serif" font-size="14" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">Photo</text></svg>'; }}/>
                <ImageListItemBar
                  title={p.caption||p.tags?.join(' ')||''}
                  actionIcon={
                    <Stack direction="row">
                      <IconButton size="small" sx={{color:'white'}} onClick={()=>updatePhoto({variables:{id:p.id,isFavourite:!p.isFavourite}})}>{p.isFavourite?<FavoriteIcon fontSize="small"/>:<FavoriteBorderIcon fontSize="small"/>}</IconButton>
                      <IconButton size="small" sx={{color:'white'}} onClick={()=>openEditPhoto(p)}><EditIcon fontSize="small"/></IconButton>
                      <IconButton size="small" sx={{color:'white'}} onClick={()=>setConfirmDelPhoto(p.id)}><DeleteIcon fontSize="small"/></IconButton>
                    </Stack>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}

      {/* Album Dialog */}
      <Dialog open={albumDialog} onClose={()=>setAlbumDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editAlbum?'Edit Album':'Create Album'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField label="Album Name *" value={albumForm.name} onChange={e=>setAlbumForm(f=>({...f,name:e.target.value}))} fullWidth/>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField select label="Album Type" value={albumForm.albumType} onChange={e=>setAlbumForm(f=>({...f,albumType:e.target.value}))} fullWidth>{ALBUM_TYPES.map(t=><MenuItem key={t} value={t}>{t.replace('_',' ')}</MenuItem>)}</TextField></Grid>
            <Grid item xs={6}><TextField select label="Ceremony" value={albumForm.ceremonyId} onChange={e=>setAlbumForm(f=>({...f,ceremonyId:e.target.value}))} fullWidth><MenuItem value="">— None —</MenuItem>{ceremonies.map(c=><MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}</TextField></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Vendor / Photographer" value={albumForm.vendorName} onChange={e=>setAlbumForm(f=>({...f,vendorName:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="Vendor Contact" value={albumForm.vendorContact} onChange={e=>setAlbumForm(f=>({...f,vendorContact:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Delivery Date" type="date" value={albumForm.deliveryDate} onChange={e=>setAlbumForm(f=>({...f,deliveryDate:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={6}><TextField label="Share URL" value={albumForm.shareUrl} onChange={e=>setAlbumForm(f=>({...f,shareUrl:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <TextField label="Description" value={albumForm.description} onChange={e=>setAlbumForm(f=>({...f,description:e.target.value}))} multiline rows={2} fullWidth/>
          <TextField label="Notes" value={albumForm.notes} onChange={e=>setAlbumForm(f=>({...f,notes:e.target.value}))} multiline rows={2} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setAlbumDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAlbum} disabled={!albumForm.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Photo Dialog */}
      <Dialog open={photoDialog} onClose={()=>setPhotoDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editPhoto?'Edit Photo':'Add Photo'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          {!editPhoto && <TextField label="Photo URL *" value={photoForm.url} onChange={e=>setPhotoForm(f=>({...f,url:e.target.value}))} fullWidth/>}
          {!editPhoto && <TextField label="Thumbnail URL" value={photoForm.thumbnail} onChange={e=>setPhotoForm(f=>({...f,thumbnail:e.target.value}))} fullWidth placeholder="Leave blank to use photo URL"/>}
          <TextField label="Caption" value={photoForm.caption} onChange={e=>setPhotoForm(f=>({...f,caption:e.target.value}))} fullWidth/>
          <TextField label="Tags (comma-separated)" value={photoForm.tags} onChange={e=>setPhotoForm(f=>({...f,tags:e.target.value}))} fullWidth placeholder="bride,ceremony,dance"/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setPhotoDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePhoto} disabled={!editPhoto&&!photoForm.url}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Deletes */}
      <Dialog open={!!confirmDel} onClose={()=>setConfirmDel(null)}>
        <DialogTitle>Delete Album?</DialogTitle>
        <DialogContent><Typography>This will remove the album and all its photos.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deleteAlbum({variables:{id:confirmDel}})}>Delete</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!confirmDelPhoto} onClose={()=>setConfirmDelPhoto(null)}>
        <DialogTitle>Delete Photo?</DialogTitle>
        <DialogActions>
          <Button onClick={()=>setConfirmDelPhoto(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deletePhoto({variables:{id:confirmDelPhoto}})}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
